import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import RichContentEditor from './RichContentEditor';
import { AdminPageHeader, AdminPageShell, NoticePanel, StatCard } from './AdminPageUI';

const ManageMidtermExams = () => {
  const navigate = useNavigate();

  // === Core Data States ===
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // === UI States ===
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [previewExam, setPreviewExam] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');

  // === Statistics ===
  const [stats, setStats] = useState({
    totalExams: 0,
    totalQuestions: 0,
    examsByClass: {},
    averageQuestions: 0
  });

  // === Form States ===
  const [examForm, setExamForm] = useState({
    className: '',
    subjectName: '',
    examType: 'Midterm',
    title: '',
    description: '',
    duration: 60, // minutes
    passingScore: 50, // percentage
    maxAttempts: 1,
    instructions: '',
    questions: []
  });

  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    questionType: 'MCQ',
    options: ['', '', '', ''],
    correctAnswer: '',
    points: 1,
    explanation: ''
  });

  // ========================= INITIALIZATION =========================
  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [exams]);

  // ========================= DATA FETCHING =========================
  const loadAllData = async () => {
    setLoading(true);
    try {
      const [examsRes, classesRes, subjectsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/Midterm`),
        fetch(`${API_ENDPOINTS.CLASSES}`),
        fetch(`${API_ENDPOINTS.SUBJECTS}`)
      ]);

      const examsData = await examsRes.json();
      const classesData = await classesRes.json();
      const subjectsData = await subjectsRes.json();

      setExams(examsData);
      setClasses(classesData);
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const examsByClass = {};
    let totalQuestions = 0;

    exams.forEach(exam => {
      if (!examsByClass[exam.className]) {
        examsByClass[exam.className] = 0;
      }
      examsByClass[exam.className]++;
      totalQuestions += exam.questions?.length || 0;
    });

    setStats({
      totalExams: exams.length,
      totalQuestions,
      examsByClass,
      averageQuestions: exams.length > 0 ? Math.round(totalQuestions / exams.length) : 0
    });
  };

  // ========================= FORM HANDLING =========================
  const handleExamFormChange = (e) => {
    const { name, value } = e.target;
    setExamForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuestionFormChange = (e) => {
    const { name, value } = e.target;
    setQuestionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...questionForm.options];
    updatedOptions[index] = value;
    setQuestionForm(prev => ({
      ...prev,
      options: updatedOptions
    }));
  };

  const openCreateModal = () => {
    setExamForm({
      className: '',
      subjectName: '',
      examType: 'Midterm',
      title: '',
      description: '',
      duration: 60,
      passingScore: 50,
      maxAttempts: 1,
      instructions: '',
      questions: []
    });
    setShowCreateModal(true);
  };

  const openEditModal = (exam) => {
    setExamForm({
      className: exam.className || '',
      subjectName: exam.subjectName || '',
      examType: exam.examType || 'Midterm',
      title: exam.title || '',
      description: exam.description || '',
      duration: exam.duration || 60,
      passingScore: exam.passingScore || 50,
      maxAttempts: exam.maxAttempts || 1,
      instructions: exam.instructions || '',
      questions: exam.questions || []
    });
    setEditingExam(exam);
    setShowEditModal(true);
  };

  const openPreviewModal = (exam) => {
    setPreviewExam(exam);
    setShowPreviewModal(true);
  };

  const validateExamForm = () => {
    const errors = [];

    if (!examForm.className) errors.push('Class is required');
    if (!examForm.subjectName) errors.push('Subject is required');
    if (!examForm.title.trim()) errors.push('Exam title is required');
    if (examForm.questions.length === 0) errors.push('At least one question is required');
    if (examForm.duration < 5) errors.push('Duration must be at least 5 minutes');
    if (examForm.passingScore < 0 || examForm.passingScore > 100) {
      errors.push('Passing score must be between 0 and 100');
    }

    return errors;
  };

  const validateQuestionForm = () => {
    const errors = [];

    if (!questionForm.questionText.trim()) errors.push('Question text is required');

    if (questionForm.questionType === 'MCQ') {
      const validOptions = questionForm.options.filter(opt => opt.trim() !== '');
      if (validOptions.length < 2) errors.push('MCQ must have at least 2 options');
      if (!questionForm.correctAnswer.trim()) errors.push('Correct answer is required for MCQ');
      if (!validOptions.includes(questionForm.correctAnswer.trim())) {
        errors.push('Correct answer must match one of the options');
      }
    } else if (!questionForm.correctAnswer.trim()) {
      errors.push('Correct answer is required');
    }

    return errors;
  };

  const addQuestionToExam = () => {
    const errors = validateQuestionForm();
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }

    const newQuestion = {
      ...questionForm,
      questionId: `q-${Date.now()}`,
      questionText: questionForm.questionText.trim(),
      correctAnswer: questionForm.correctAnswer.trim(),
      options: questionForm.questionType === 'MCQ' ? questionForm.options.filter(opt => opt.trim() !== '') : []
    };

    setExamForm(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));

    // Reset question form
    setQuestionForm({
      questionText: '',
      questionType: 'MCQ',
      options: ['', '', '', ''],
      correctAnswer: '',
      points: 1,
      explanation: ''
    });
  };

  const removeQuestionFromExam = (questionId) => {
    setExamForm(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.questionId !== questionId)
    }));
  };

  const handleSaveExam = async () => {
    const errors = validateExamForm();
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }

    try {
      const examData = {
        ...examForm,
        title: examForm.title.trim(),
        description: examForm.description.trim(),
        instructions: examForm.instructions.trim()
      };

      let response;
      if (editingExam) {
        response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/Midterm/${editingExam.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(examData)
        });
      } else {
        response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/Midterm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(examData)
        });
      }

      if (response.ok) {
        setShowCreateModal(false);
        setShowEditModal(false);
        setEditingExam(null);
        loadAllData();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to save exam');
      }
    } catch (error) {
      console.error('Error saving exam:', error);
      alert('Failed to save exam. Please try again.');
    }
  };

  const handleDeleteExam = async (exam) => {
    if (!window.confirm(`Are you sure you want to delete "${exam.title}"Loading This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/Midterm/${exam.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadAllData();
      } else {
        alert('Failed to delete exam');
      }
    } catch (error) {
      console.error('Error deleting exam:', error);
      alert('Failed to delete exam. Please try again.');
    }
  };

  // ========================= FILTERING =========================
  const getFilteredExams = () => {
    let filtered = exams;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(exam =>
        exam.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exam.subjectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exam.className.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by class
    if (selectedClass !== 'all') {
      filtered = filtered.filter(exam => exam.className === selectedClass);
    }

    return filtered;
  };

  // ========================= HELPERS =========================
  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case 'MCQ': return 'Note';
      case 'True/False': return '??';
      case 'Short Answer': return 'Note';
      case 'Essay': return 'Essay';
      default: return 'Help';
    }
  };

  const getClassSubjects = (className) => {
    return subjects.filter(subject => subject.className === className);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading exam management...</div>
      </div>
    );
  }

  const filteredExams = getFilteredExams();

  return (
    <AdminPageShell>
      <AdminPageHeader
        eyebrow="Assessment"
        title="Manage Midterm Exams"
        description="Create, review, preview, and maintain midterm assessments by class and subject."
        actions={(
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700"
            >
              Back to Dashboard
            </button>
        )}
      />

        {stats.totalExams === 0 || stats.averageQuestions < 5 ? (
          <NoticePanel title="Assessment readiness" tone="amber">
            Add exams with enough questions per class and subject before opening the midterm window.
          </NoticePanel>
        ) : null}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Total Exams" value={stats.totalExams} tone="sky" detail="Midterm papers" />
          <StatCard label="Questions" value={stats.totalQuestions} tone="emerald" detail="Across all exams" />
          <StatCard label="Avg Questions" value={stats.averageQuestions} tone={stats.averageQuestions < 5 ? 'rose' : 'violet'} detail="Per exam" />
          <StatCard label="Active Classes" value={Object.keys(stats.examsByClass).length} tone="amber" detail="With midterm exams" />
        </div>

        {/* Controls */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Exams</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title, subject, or class..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Class</label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Classes</option>
                  {classes.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={openCreateModal}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200 flex items-center space-x-2"
            >
              <span>+</span>
              <span>Create Exam</span>
            </button>
          </div>
        </div>

        {/* Exams Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredExams.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
              <div className="text-6xl mb-4">Note</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {exams.length === 0 ? 'No Exams Yet' : 'No Exams Found'}
              </h3>
              <p className="text-gray-600 mb-6">
                {exams.length === 0
                  ? 'Create your first midterm exam to get started.'
                  : 'Try adjusting your search or filter criteria.'}
              </p>
              {exams.length === 0 && (
                <button
                  onClick={openCreateModal}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                >
                  Create First Exam
                </button>
              )}
            </div>
          ) : (
            filteredExams.map((exam) => (
              <div key={exam.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200">
                <div className="p-6">
                  {/* Exam Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{exam.title}</h3>
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <span>{exam.className}</span>
                        <span>?</span>
                        <span>{exam.subjectName}</span>
                      </div>
                    </div>
                  </div>

                  {/* Exam Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Questions:</span>
                      <span className="font-medium">{exam.questions?.length || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{exam.duration || 60} min</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Passing Score:</span>
                      <span className="font-medium">{exam.passingScore || 50}%</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openPreviewModal(exam)}
                      className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition duration-200 text-sm font-medium"
                    >
                      Preview
                    </button>
                    <button
                      onClick={() => openEditModal(exam)}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteExam(exam)}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition duration-200 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Results Summary */}
        {filteredExams.length > 0 && (
          <div className="mt-6 text-center text-gray-600">
            Showing {filteredExams.length} of {exams.length} exams
          </div>
        )}
      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {showCreateModal ? 'Create New Midterm Exam' : 'Edit Exam'}
              </h3>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Note Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Exam Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={examForm.title}
                        onChange={handleExamFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter exam title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Class <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="className"
                        value={examForm.className}
                        onChange={handleExamFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Class</option>
                        {classes.map(cls => (
                          <option key={cls} value={cls}>{cls}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="subjectName"
                        value={examForm.subjectName}
                        onChange={handleExamFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!examForm.className}
                      >
                        <option value="">Select Subject</option>
                        {getClassSubjects(examForm.className).map(subject => (
                          <option key={subject.id} value={subject.name}>{subject.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration (minutes)
                      </label>
                      <input
                        type="number"
                        name="duration"
                        value={examForm.duration}
                        onChange={handleExamFormChange}
                        min="5"
                        max="300"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Passing Score (%)
                      </label>
                      <input
                        type="number"
                        name="passingScore"
                        value={examForm.passingScore}
                        onChange={handleExamFormChange}
                        min="0"
                        max="100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Attempts
                      </label>
                      <input
                        type="number"
                        name="maxAttempts"
                        value={examForm.maxAttempts}
                        onChange={handleExamFormChange}
                        min="1"
                        max="10"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={examForm.description}
                      onChange={handleExamFormChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Brief description of the exam..."
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instructions
                    </label>
                    <textarea
                      name="instructions"
                      value={examForm.instructions}
                      onChange={handleExamFormChange}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Special instructions for students..."
                    />
                  </div>
                </div>

                {/* Questions Section */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Help Questions ({examForm.questions.length})</h4>

                  {/* Add Question Form */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h5 className="font-medium text-gray-800 mb-3">Add New Question</h5>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Question Type
                        </label>
                        <select
                          name="questionType"
                          value={questionForm.questionType}
                          onChange={handleQuestionFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="MCQ">Multiple Choice (MCQ)</option>
                          <option value="True/False">True/False</option>
                          <option value="Short Answer">Short Answer</option>
                          <option value="Essay">Essay</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Points
                        </label>
                        <input
                          type="number"
                          name="points"
                          value={questionForm.points}
                          onChange={handleQuestionFormChange}
                          min="1"
                          max="10"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question Text <span className="text-red-500">*</span>
                      </label>
                      <RichContentEditor
                        value={questionForm.questionText}
                        onChange={(value) => setQuestionForm(prev => ({ ...prev, questionText: value }))}
                        placeholder="Enter the question text... You can paste images and videos directly!"
                      />
                    </div>

                    {/* MCQ Options */}
                    {questionForm.questionType === 'MCQ' && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Options (at least 2 required)
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {questionForm.options.map((option, index) => (
                            <input
                              key={index}
                              type="text"
                              value={option}
                              onChange={(e) => handleOptionChange(index, e.target.value)}
                              placeholder={`Option ${index + 1}`}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Correct Answer */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Correct Answer <span className="text-red-500">*</span>
                      </label>
                      {questionForm.questionType === 'MCQ' ? (
                        <select
                          name="correctAnswer"
                          value={questionForm.correctAnswer}
                          onChange={handleQuestionFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select correct option</option>
                          {questionForm.options
                            .map((option, index) => ({ option, index }))
                            .filter(({ option }) => option.trim() !== '')
                            .map(({ option, index }) => (
                              <option key={index} value={option}>{option}</option>
                            ))}
                        </select>
                      ) : (
                        <textarea
                          name="correctAnswer"
                          value={questionForm.correctAnswer}
                          onChange={handleQuestionFormChange}
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter the correct answer..."
                        />
                      )}
                    </div>

                    <button
                      onClick={addQuestionToExam}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-200"
                    >
                      Add Question
                    </button>
                  </div>

                  {/* Questions List */}
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {examForm.questions.map((question, index) => (
                      <div key={question.questionId} className="bg-white border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">{getQuestionTypeIcon(question.questionType)}</span>
                            <span className="font-medium text-gray-900">
                              Question {index + 1} ({question.points} pts)
                            </span>
                          </div>
                          <button
                            onClick={() => removeQuestionFromExam(question.questionId)}
                            className="text-red-500 hover:text-red-700"
                          >
                            ✕
                          </button>
                        </div>

                        <div
                          className="text-gray-800 mb-2"
                          dangerouslySetInnerHTML={{ __html: question.questionText }}
                        />

                        {question.questionType === 'MCQ' && question.options && (
                          <div className="mb-2">
                            <p className="text-sm text-gray-600 mb-1">Options:</p>
                            <ul className="text-sm text-gray-700 ml-4">
                              {question.options.map((option, optIndex) => (
                                <li key={optIndex} className={option === question.correctAnswer ? 'text-green-600 font-medium' : ''}>
                                  {String.fromCharCode(97 + optIndex)}) {option}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <p className="text-sm text-green-600">
                          Correct Answer: {question.correctAnswer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleSaveExam}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    {showCreateModal ? 'Create Exam' : 'Update Exam'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setEditingExam(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewExam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Exam Preview: {previewExam.title}</h3>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Exam Header */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="text-lg font-semibold text-blue-900 mb-2">{previewExam.title}</h4>
                <p className="text-blue-800 mb-2">{previewExam.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><strong>Class:</strong> {previewExam.className}</div>
                  <div><strong>Subject:</strong> {previewExam.subjectName}</div>
                  <div><strong>Duration:</strong> {previewExam.duration} min</div>
                  <div><strong>Questions:</strong> {previewExam.questions?.length || 0}</div>
                </div>
                {previewExam.instructions && (
                  <div className="mt-4">
                    <strong>Instructions:</strong>
                    <p className="text-blue-700 mt-1">{previewExam.instructions}</p>
                  </div>
                )}
              </div>

              {/* Questions Preview */}
              <div className="space-y-6">
                {previewExam.questions?.map((question, index) => (
                  <div key={question.questionId} className="border rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="text-lg">{getQuestionTypeIcon(question.questionType)}</span>
                      <span className="font-medium">Question {index + 1} ({question.points} points)</span>
                    </div>

                    <div
                      className="text-gray-800 mb-4"
                      dangerouslySetInnerHTML={{ __html: question.questionText }}
                    />

                    {question.questionType === 'MCQ' && question.options && (
                      <div className="space-y-2">
                        {question.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name={`question-${index}`}
                              className="text-blue-600"
                              disabled
                            />
                            <label className="text-gray-700">
                              {String.fromCharCode(97 + optIndex)}) {option}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}

                    {question.questionType === 'True/False' && (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input type="radio" name={`question-${index}`} className="text-blue-600" disabled />
                          <label className="text-gray-700">True</label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input type="radio" name={`question-${index}`} className="text-blue-600" disabled />
                          <label className="text-gray-700">False</label>
                        </div>
                      </div>
                    )}

                    {(question.questionType === 'Short Answer' || question.questionType === 'Essay') && (
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        placeholder="Student answer area..."
                        rows={question.questionType === 'Essay' ? 6 : 3}
                        disabled
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPageShell>
  );
};

export default ManageMidtermExams;
